import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  ReactFlow,
  addEdge,
  Background,
  useNodesState,
  useEdgesState,
  MiniMap,
  Controls,
  Panel,
} from '@xyflow/react';
import ContentNode from '../components/ContentNode';
import GroupNode from '../components/GroupNode';
import MethodGroupNode from '../components/MethodGroupNode';
import MethodCallNode from '../components/MethodCallNode';
import {
  useColorMode,
  PALETTE,
  CLASS_COLORS,
} from '../contexts/ColorModeContext';
import { useInspect } from '../contexts/InspectContext';
import { browserStorage } from '../services/browserStorage';
import { fileStorage } from '../services/fileStorage';
import { LAYOUT_CONFIG } from '../shared-config';
import { Box } from '@mui/material';
import { getCookie } from '../utils/cookies';

import '@xyflow/react/dist/style.css';

// Function to apply styling to nodes and edges based on their data
const applyStyling = (
  nodes,
  edges,
  colorMode,
  inspectedMethodId,
  savedPositions = null,
  shouldEnableMethodInteractions = false
) => {
  // Use saved positions if provided, otherwise use empty object
  const positionMap = savedPositions || {};

  // Transform the data structure to create deep hierarchy
  const transformedNodes: object[] = [];
  const transformedEdges: object[] = [];

  // Define consistent spacing constants
  const callNodeHeight = LAYOUT_CONFIG.callNodeHeight; // Height for both invoke and call nodes
  const methodCallSpacing = LAYOUT_CONFIG.methodCallSpacing; // Spacing between method calls
  const baseHeight = LAYOUT_CONFIG.baseMemberHeight; // Base height for method group header

  // Track class colors
  const classColorMap = new Map();
  let classIndex = 0;

  // Group nodes by class and method
  const classGroups = new Map();
  const methodGroups = new Map();
  const methodCallNodes = new Map();

  // First pass: organize nodes by class and method
  const classMethodCounts = new Map(); // Track method count per class
  const classMethodGroups = new Map(); // Track method groups per class for positioning
  const globalMethodCount = { count: 0 }; // Track total method count for global positioning

  nodes.forEach((node) => {
    if (node.type === 'group' && node.data.type === 'class') {
      // This is a class node - initialize tracking and assign color
      classMethodCounts.set(node.id, 0);
      classMethodGroups.set(node.id, []);

      // Assign color to this class
      const colorIndex = classIndex % CLASS_COLORS.length;
      classColorMap.set(node.id, CLASS_COLORS[colorIndex]);
      classIndex++;
    } else if (node.type === 'content' && node.data.type === 'method') {
      // This is a method node - create a method group for it
      const methodGroupId = `${node.id}_group`;
      const methodCount = classMethodCounts.get(node.parentId) || 0;
      classMethodCounts.set(node.parentId, methodCount + 1);

      const methodGroup = {
        id: methodGroupId,
        type: 'methodGroup',
        draggable: shouldEnableMethodInteractions, // Disable dragging for method nodes
        selectable: shouldEnableMethodInteractions, // Enable selection only when zoomed in
        connectable: shouldEnableMethodInteractions, // Enable connections only when zoomed in
        data: {
          label: node.data.label,
          type: 'method',
          accessModifier: node.data.accessModifier,
          returnType: node.data.returnType,
          arguments: node.data.arguments,
          className: node.data.className,
          classId: node.parentId, // Store the class ID for positioning
          classColor: classColorMap.get(node.parentId), // Pass the class color
          methodId: node.id, // Store the original method ID for inspect functionality
          filePath: node.data.filePath,
          line: node.data.line,
        },
        style: {
          backgroundColor:
            colorMode === 'dark'
              ? PALETTE.dark.background
              : PALETTE.light.background,
          border: `1px solid ${classColorMap.get(node.parentId).light ?? '#ff8c00'}`,
          borderRadius: '4px',
          padding: '8px',
          minWidth: 250,
          boxShadow: 'none',
        },
      };

      methodGroups.set(methodGroupId, methodGroup);

      // Add to class's method groups list
      const classGroups = classMethodGroups.get(node.parentId) || [];
      classGroups.push(methodGroup);
      classMethodGroups.set(node.parentId, classGroups);
    }
  });

  // Second pass: create Invoke nodes for each method group
  const invokeNodes = new Map();

  methodGroups.forEach((methodGroup, methodGroupId) => {
    const invokeNodeId = `invoke_${methodGroupId}`;
    const invokeMethodGroup = methodGroups.get(methodGroupId);
    const invokeClassColor = invokeMethodGroup?.data.classColor;

    const invokeNode = {
      id: invokeNodeId,
      type: 'methodCall',
      draggable: shouldEnableMethodInteractions, // Disable dragging for invoke nodes
      selectable: shouldEnableMethodInteractions, // Enable selection only when zoomed in
      connectable: shouldEnableMethodInteractions, // Enable connections only when zoomed in
      data: {
        label: 'Invoke',
        type: 'invoke',
        methodGroupId: methodGroupId,
        targetClassColor: invokeClassColor,
      },
      position: {
        x: 20,
        y: 20, // Position at the top of the method group
      },
      parentId: methodGroupId,
      extent: 'parent',
      style: {
        backgroundColor: 'transparent',
        border: `1px solid ${methodGroup.data.accessModifier === 'public' ? invokeClassColor?.light : invokeClassColor?.dark}`,
        color: colorMode === 'dark' ? 'white' : 'black',
        boxShadow: 'none',
      },
    };

    invokeNodes.set(invokeNodeId, invokeNode);
  });

  // Third pass: create method call nodes from edges
  const methodCallCounts = new Map(); // Track call count per method group

  edges.forEach((edge) => {
    if (edge.data.type === 'method-call') {
      // Find the source method group
      const sourceMethodGroupId = `${edge.source}_group`;
      const sourceMethodGroup = methodGroups.get(sourceMethodGroupId);

      if (sourceMethodGroup) {
        // Get current call count for this method group
        const callCount = methodCallCounts.get(sourceMethodGroupId) || 0;
        methodCallCounts.set(sourceMethodGroupId, callCount + 1);

        // Create a method call node with organized positioning
        const callNodeId = `call_${edge.source}_${edge.target}`;

        // Find the target class color
        const targetMethodGroupId = `${edge.target}_group`;
        const targetMethodGroup = methodGroups.get(targetMethodGroupId);
        const targetClassColor = targetMethodGroup?.data.classColor;

        const callNode = {
          id: callNodeId,
          type: 'methodCall',
          draggable: shouldEnableMethodInteractions, // Disable dragging for method call nodes
          selectable: shouldEnableMethodInteractions, // Enable selection only when zoomed in
          connectable: shouldEnableMethodInteractions, // Enable connections only when zoomed in
          data: {
            label: `${edge.data.label}`,
            type: 'method-call',
            targetMethod: edge.target,
            sourceMethod: edge.source,
            targetClassColor: targetClassColor, // Pass the target class color
          },
          position: {
            x: 20, // Single column layout
            y:
              20 +
              callNodeHeight +
              methodCallSpacing +
              callCount * (callNodeHeight + methodCallSpacing), // Consistent spacing
          },
          parentId: sourceMethodGroupId,
          extent: 'parent',
          style: {
            backgroundColor:
              targetMethodGroup.data.accessModifier === 'public'
                ? targetClassColor?.light
                : targetClassColor?.dark,
            color: targetClassColor?.contrastText,
            border: `1px solid ${targetMethodGroup.data.accessModifier === 'public' ? targetClassColor?.light : targetClassColor?.dark}`,
            boxShadow: 'none',
          },
        };

        methodCallNodes.set(callNodeId, callNode);
        sourceMethodGroup.methodCalls = sourceMethodGroup.methodCalls || [];
        sourceMethodGroup.methodCalls.push(callNode);
      }
    }
  });

  // Fourth pass: calculate method group heights first
  const methodGroupHeights = new Map();

  // Calculate heights for all method groups based on their call nodes
  methodGroups.forEach((methodGroup, methodGroupId) => {
    // Count how many call nodes this method group will have
    const callCount = methodCallCounts.get(methodGroupId) || 0;
    const calculatedHeight =
      baseHeight +
      callCount * (callNodeHeight + methodCallSpacing) +
      methodCallSpacing;
    methodGroupHeights.set(methodGroupId, calculatedHeight);
  });

  // Third pass: create class group nodes and position method groups within them
  const classGroupNodes = [];
  const methodWidth = LAYOUT_CONFIG.methodWidth;
  const methodSpacing = LAYOUT_CONFIG.methodSpacing;
  const publicPrivateGap = LAYOUT_CONFIG.publicPrivateGap; // Gap between public and private columns within a class

  // Use server positioning for class groups instead of calculating our own
  const classGroupNodesMap = new Map();

  classMethodGroups.forEach((classMethodGroupsList, classId) => {
    // Skip if we don't have a valid class name
    const className = classMethodGroupsList[0]?.data.className;
    if (!className || className === 'Unknown Class') {
      return; // Skip this class group entirely
    }

    const publicMethods = classMethodGroupsList.filter(
      (group) => group.data.accessModifier === 'public'
    );
    const privateMethods = classMethodGroupsList.filter(
      (group) =>
        group.data.accessModifier === 'private' ||
        group.data.accessModifier === 'internal'
    );
    const protectedMethods = classMethodGroupsList.filter(
      (group) => group.data.accessModifier === 'protected'
    );

    // Determine which columns are actually needed
    const hasPublicMethods = publicMethods.length > 0;
    const hasProtectedMethods = protectedMethods.length > 0;
    const hasPrivateMethods = privateMethods.length > 0;
    const columnCount = [
      hasPublicMethods,
      hasProtectedMethods,
      hasPrivateMethods,
    ].filter(Boolean).length;

    // Calculate heights for public methods
    let publicColumnHeight = 40; // Start with padding
    publicMethods.forEach((methodGroup) => {
      const methodHeight = methodGroupHeights.get(methodGroup.id) || 70;
      publicColumnHeight += methodHeight + methodSpacing;
    });
    publicColumnHeight -= methodSpacing;

    // Calculate heights for private methods
    let privateColumnHeight = 40; // Start with padding
    privateMethods.forEach((methodGroup) => {
      const methodHeight = methodGroupHeights.get(methodGroup.id) || 70;
      privateColumnHeight += methodHeight + methodSpacing;
    });
    privateColumnHeight -= methodSpacing;

    // Calculate heights for protected methods
    let protectedColumnHeight = 40; // Start with padding
    protectedMethods.forEach((methodGroup) => {
      const methodHeight = methodGroupHeights.get(methodGroup.id) || 70;
      protectedColumnHeight += methodHeight + methodSpacing;
    });
    protectedColumnHeight -= methodSpacing;

    // Class group height should be the maximum of the columns that exist plus padding
    const classGroupHeight =
      Math.max(publicColumnHeight, privateColumnHeight, protectedColumnHeight) +
      60; // 40px for padding

    // Calculate dynamic width based on actual columns needed
    const dynamicClassWidth =
      columnCount > 0
        ? methodWidth * columnCount + publicPrivateGap * (columnCount - 1) + 40 // columns + gaps + padding
        : methodWidth + 40; // minimum width if no methods

    // Find the corresponding server node to get its position
    const serverClassNode = nodes.find(
      (node) =>
        node.type === 'group' && node.data && node.data.label === className
    );

    // Use server position if available, otherwise use a default
    const classPosition = serverClassNode
      ? serverClassNode.position
      : { x: 100, y: 100 };

    // Create class group node
    const classGroupNode = {
      id: `class_group_${classId}`,
      type: 'group',
      data: {
        label: className,
        classColor: classColorMap.get(classId),
      },
      position: classPosition,
      style: {
        width: dynamicClassWidth,
        height: classGroupHeight,
        backgroundColor:
          colorMode === 'dark'
            ? PALETTE.dark.background
            : PALETTE.light.background,
        border: `2px solid ${classColorMap.get(classId).light ?? '#6496ff'}`,
        color: classColorMap.get(classId).contrastText,
        borderRadius: '4px',
        padding: '10px',
        boxShadow: 'none',
      },
    };

    classGroupNodes.push(classGroupNode);
    classGroupNodesMap.set(classId, classGroupNode);

    // Position public methods in the left column within class group
    let currentPublicY = 40;
    publicMethods.forEach((methodGroup, index) => {
      methodGroup.position = {
        x: 20, // Left column within class group
        y: currentPublicY,
      };
      methodGroup.parentId = classGroupNode.id; // Make it a child of the class group

      // Move to next method position based on this method's actual height
      const methodHeight = methodGroupHeights.get(methodGroup.id) || 70;
      currentPublicY += methodHeight + methodSpacing;
      globalMethodCount.count++;
    });

    // Position protected methods in the middle column within class group (if it exists)
    let currentProtectedY = 40;
    protectedMethods.forEach((methodGroup, index) => {
      const protectedColumnX = hasPublicMethods
        ? methodWidth + publicPrivateGap + 20
        : 20;
      methodGroup.position = {
        x: protectedColumnX, // Middle column within class group (or left if no public methods)
        y: currentProtectedY,
      };
      methodGroup.parentId = classGroupNode.id; // Make it a child of the class group

      // Move to next method position based on this method's actual height
      const methodHeight = methodGroupHeights.get(methodGroup.id) || 70;
      currentProtectedY += methodHeight + methodSpacing;
      globalMethodCount.count++;
    });

    // Position private methods in the right column within class group (if it exists)
    let currentPrivateY = 40;
    privateMethods.forEach((methodGroup, index) => {
      let privateColumnX;
      if (hasPublicMethods && hasProtectedMethods) {
        // Public, Protected, Private
        privateColumnX = methodWidth * 2 + publicPrivateGap * 2 + 20;
      } else if (hasPublicMethods || hasProtectedMethods) {
        // Public, Private OR Protected, Private
        privateColumnX = methodWidth + publicPrivateGap + 20;
      } else {
        // Only Private
        privateColumnX = 20;
      }

      methodGroup.position = {
        x: privateColumnX, // Right column within class group (or appropriate position)
        y: currentPrivateY,
      };
      methodGroup.parentId = classGroupNode.id; // Make it a child of the class group

      // Move to next method position based on this method's actual height
      const methodHeight = methodGroupHeights.get(methodGroup.id) || 70;
      currentPrivateY += methodHeight + methodSpacing;
      globalMethodCount.count++;
    });
  });

  // Fourth pass: build the final node structure
  // Add class group nodes first
  classGroupNodes.forEach((classGroupNode) => {
    transformedNodes.push(classGroupNode);
  });

  // Add method groups (now children of class groups)
  methodGroups.forEach((methodGroup) => {
    transformedNodes.push(methodGroup);
  });

  // Add Invoke nodes
  invokeNodes.forEach((invokeNode) => {
    transformedNodes.push(invokeNode);
  });

  // Add method call nodes
  methodCallNodes.forEach((callNode) => {
    transformedNodes.push(callNode);
  });

  // Sixth pass: calculate dynamic dimensions for groups
  const nodesWithDynamicGroups = transformedNodes.map((node) => {
    if (node.type === 'methodGroup') {
      // Use the pre-calculated height
      const calculatedHeight = methodGroupHeights.get(node.id) || 90; // Reduced fallback height

      return {
        ...node,
        style: {
          ...node.style,
          width: 250,
          height: calculatedHeight,
        },
      };
    }
    return node;
  });

  // Fifth pass: create edges between method call nodes and Invoke nodes
  methodCallNodes.forEach((callNode) => {
    const targetMethodGroupId = `${callNode.data.targetMethod}_group`;
    const targetInvokeNodeId = `invoke_${targetMethodGroupId}`;
    const targetInvokeNode = invokeNodes.get(targetInvokeNodeId);

    if (targetInvokeNode) {
      // Determine edge color based on inspect mode
      let edgeColor = '#ff6b6b'; // Default red
      let markerColor = '#ff6b6b';

      if (inspectedMethodId) {
        // Check if this edge leads to the inspected method
        // The targetMethod is the original method ID, not the group ID
        const isRelevantEdge = callNode.data.targetMethod === inspectedMethodId;
        if (isRelevantEdge) {
          edgeColor = '#ff4444'; // Bright red for relevant edges
          markerColor = '#ff4444';
        } else {
          edgeColor = '#666666'; // Gray for irrelevant edges
          markerColor = '#666666';
        }
      }

      transformedEdges.push({
        id: `e-${callNode.id}-${targetInvokeNodeId}`,
        source: callNode.id,
        target: targetInvokeNodeId,
        // type: 'smoothstep',
        style: {
          stroke: edgeColor,
          strokeWidth: 1,
        },
        markerEnd: {
          type: 'arrowclosed',
          color: markerColor,
        },
        data: {
          type: 'method-call',
          label: 'calls',
          targetMethod: callNode.data.targetMethod, // Add the target method ID for inspect functionality
        },
      });
    }
  });

  return {
    nodes: nodesWithDynamicGroups.map((node) => {
      // Check if we have a saved position for this node
      const savedPosition = positionMap[node.id];
      const finalPosition = savedPosition || node.position;

      return {
        ...node,
        position: finalPosition,
        style: {
          ...node.style,
          // TODO: figure out the best option:
          // backgroundColor: colorMode === 'dark' ? PALETTE.dark.background : PALETTE.light.background,
          // color: colorMode === 'dark' ? PALETTE.dark.text : PALETTE.light.text,
        },
      };
    }),
    edges: transformedEdges.map((edge) => ({
      ...edge,
      style: {
        ...edge.style,
        // TODO: figure out the best option:
        // stroke: colorMode === 'dark' ? PALETTE.dark.edge : PALETTE.light.edge,
      },
    })),
  };
};

const NestedFlowDemo2 = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesState] = useEdgesState([]);
  const { colorMode, setColorMode } = useColorMode();
  const serverUrl = process.env.REACT_APP_API_BASE || ''; // default to whatever address the app is running on
  const { inspectedMethodId } = useInspect();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState<(File & {path: string})[]>([]);
  const [showServerSettings, setShowServerSettings] = useState(false);
  const [areStylesOutdated, setAreStylesOutdated] = useState(false);
  const [showServerWakeMessage, setShowServerWakeMessage] = useState(false);
  const snapGrid = [20, 20];
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [shouldEnableMethodInteractions, setShouldEnableMethodInteractions] = useState(false);
  const ZOOM_THRESHOLD = 0.8; // Threshold for enabling method node interactions

  // Add a ref to track if we've already tried auto-generating the graph
  const didAutoGenerate = useRef(false);

  const loadGraphWithFiles = async (
    files,
    showErrors = true,
    isInitialLoad = false
  ) => {
    try {
      if (!isInitialLoad) {
        setIsLoading(true);
      }
      setError('');

      if (files.length === 0) {
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${serverUrl}/api/generate-graph`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          files: files,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate graph from server');
      }

      const data = await response.json();

      if (!data.nodes || !data.edges) {
        throw new Error('Invalid response from server');
      }

      // Clear saved positions when loading new files (unless it's initial load)
      if (!isInitialLoad) {
        // fileStorage.remove('nodePositions');
        browserStorage.remove('nodePositions');
      }

      // Load saved positions before applying styling
      let savedPositions = null;
      try {
        savedPositions = await browserStorage.get('nodePositions');
        if (!savedPositions) {
          savedPositions = await fileStorage.getPresetData(selectedPreset);
        }
      } catch (error) {
        console.warn('Could not load saved positions:', error);
      }
      // Apply styling to the raw data
      const { nodes: styledNodes, edges: styledEdges } = applyStyling(
        data.nodes,
        data.edges,
        colorMode,
        inspectedMethodId,
        savedPositions
      );

      setNodes(styledNodes);
      setEdges(styledEdges);
    } catch (error) {
      console.error('Error loading graph:', error);
      if (showErrors) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateGraph = async () => {
    if (selectedFiles.length === 0 && !selectedPreset) {
      alert('Please select at least one file to analyze.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${serverUrl}/api/generate-graph`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          files: selectedFiles,
          preset: selectedPreset,
        }),
      }).catch((error) => {
        console.error('Error generating graph:', error);
        setError(error.message);
        setIsLoading(false);
      });

      // if (!response.ok) {
      //   console.log('response', response);
      //   throw new Error(`HTTP error! status: ${response.status}`);
      // }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.nodes || !data.edges) {
        throw new Error('Invalid response from server');
      }

      // Load saved positions before applying styling
      let savedPositions = null;
      try {
        savedPositions = await browserStorage.get(
          selectedPreset ? `nodePositions-${selectedPreset}` : 'nodePositions'
        );
        if (!savedPositions && selectedPreset) {
          savedPositions = await fileStorage.getPresetData(selectedPreset);
        }
      } catch (error) {
        console.warn('Could not load saved positions:', error);
      }
      // Apply styling to the raw data
      const { nodes: styledNodes, edges: styledEdges } = applyStyling(
        data.nodes,
        data.edges,
        colorMode,
        inspectedMethodId,
        savedPositions
      );

      setNodes(styledNodes);
      setEdges(styledEdges);
    } catch (error) {
      console.error('Error generating graph:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to update edge styles for inspect mode without recreating the entire graph
  const updateEdgeStyles = useCallback(() => {
    if (nodes.length > 0 && edges.length > 0) {
      setEdges((currentEdges) => {
        return currentEdges.map((edge) => {
          // Only update method-call edges
          if (edge.data?.type === 'method-call') {
            let edgeColor = '#ff6b6b'; // Default red
            let markerColor = '#ff6b6b';

            if (inspectedMethodId) {
              // Check if this edge leads to the inspected method
              // The targetMethod is the original method ID, not the group ID
              const isRelevantEdge =
                edge.data.targetMethod === inspectedMethodId;
              if (isRelevantEdge) {
                edgeColor = '#ff4444'; // Bright red for relevant edges
                markerColor = '#ff4444';
              } else {
                edgeColor = '#666666'; // Gray for irrelevant edges
                markerColor = '#666666';
              }
            }

            return {
              ...edge,
              style: {
                ...edge.style,
                stroke: edgeColor,
              },
              markerEnd: {
                ...edge.markerEnd,
                color: markerColor,
              },
            };
          }
          return edge;
        });
      });
    }
  }, [nodes.length, edges.length, inspectedMethodId, setEdges]);

  // Load demo data based on URL parameters on mount (only once)
  useEffect(() => {
    if (didAutoGenerate.current) return;
    didAutoGenerate.current = true;

    const loadDemoData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const demoParam = urlParams.get('demo');
        
        if (demoParam === 'nestjs' || demoParam === 'cs') {
          // Load preset demo
          setSelectedPreset(demoParam);
          setIsLoading(false);
        } else if (demoParam === 'custom') {
          console.log('custom');
          // Load custom files from cookies
          const uploadedFilesFromCookie = getCookie('codemaps-files');
          if (uploadedFilesFromCookie) {
            try {
              const files = JSON.parse(uploadedFilesFromCookie);
              if (files.length > 0) {
                setUploadedFiles(files);
                setSelectedFiles(files.map(file => file.path));
                setIsLoading(false);
              } else {
                setIsLoading(false);
              }
            } catch (error) {
              console.error('Error parsing uploaded files from cookie:', error);
              setIsLoading(false);
            }
          } else {
            setIsLoading(false);
          }
        } else {
          // No demo parameter, just set loading to false
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading demo data:', error);
        setIsLoading(false);
      }
    };

    loadDemoData();
  }, []);

  // Auto-generate graph when preset is set
  useEffect(() => {
    if (selectedPreset && !nodes.length) {
      generateGraph();
    }
  }, [selectedPreset, nodes.length]);

  // Auto-generate graph when custom files are loaded
  useEffect(() => {
    if (selectedFiles.length > 0 && !selectedPreset && !nodes.length) {
      loadGraphWithFiles(selectedFiles, false, true);
    }
  }, [selectedFiles, selectedPreset, nodes.length]);

  // Show a hint if loading takes longer than a few seconds (e.g. server waking up)
  useEffect(() => {
    if (!isLoading) {
      setShowServerWakeMessage(false);
      return;
    }

    setShowServerWakeMessage(false);
    const timeoutId = setTimeout(() => {
      setShowServerWakeMessage(true);
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading]);

  // Define node types
  const nodeTypes = {
    content: ContentNode,
    group: GroupNode,
    methodGroup: MethodGroupNode,
    methodCall: MethodCallNode,
  };

  // Update edge styles when inspect state changes
  useEffect(() => {
    if (nodes.length > 0 && edges.length > 0) {
      updateEdgeStyles();
    }
  }, [inspectedMethodId, updateEdgeStyles]);

  useEffect(() => {
    if (nodes.length > 0 && edges.length > 0 && areStylesOutdated) {
      generateGraph();
      setAreStylesOutdated(false);
    }
  }, [colorMode, nodes, edges, areStylesOutdated]);

  // Update node interaction settings when zoom changes
  useEffect(() => {
    if (nodes.length > 0) {
      setNodes((currentNodes) => {
        return currentNodes.map((node) => {
          // Only update method-related nodes
          if (['methodGroup', 'methodCall'].includes(node.type)) {
                return {
              ...node,
              draggable: shouldEnableMethodInteractions,
              selectable: shouldEnableMethodInteractions,
              connectable: shouldEnableMethodInteractions,
            };
          }
          return node;
        });
      });
    }
  }, [shouldEnableMethodInteractions, setNodes]);



  const onConnect = useCallback((connection) => {
    setEdges((eds) => addEdge(connection, eds));
  }, []);

  // Handle zoom changes
  const onMove = useCallback((event, viewport) => {
    const newShouldEnableInteractions = viewport.zoom >= ZOOM_THRESHOLD;
    if (newShouldEnableInteractions !== shouldEnableMethodInteractions) {
      setShouldEnableMethodInteractions(newShouldEnableInteractions);
    }
  }, [shouldEnableMethodInteractions]);

  // Debounced function to save node positions
  const debouncedSavePositions = useRef(null);

  // Custom handler to save node positions when they change
  const handleNodesChange = useCallback(
    (changes) => {
      // First apply the changes using the original onNodesChange
      onNodesChange(changes);

      // Clear any existing timeout
      if (debouncedSavePositions.current) {
        clearTimeout(debouncedSavePositions.current);
      }

      // Debounce the position saving - only save after 500ms of no movement
      debouncedSavePositions.current = setTimeout(() => {
        setNodes((nds) => {
          const positionMap = {};
          nds.forEach((node) => {
            positionMap[node.id] = node.position;
          });
          // fileStorage.set('nodePositions', positionMap);
          browserStorage.set(
            selectedPreset
              ? `nodePositions-${selectedPreset}`
              : 'nodePositions',
            positionMap
          );
          return nds;
        });
      }, 500);
    },
    [onNodesChange, setNodes, selectedPreset]
  );

  const onChange = (evt) => {
    setColorMode(evt.target.value);
    setAreStylesOutdated(true);
  };

  const handleResetGraph = async () => {
    // Clear saved positions
    // fileStorage.remove('nodePositions');
    browserStorage.remove('nodePositions');

    // Regenerate graph with current files
    if (selectedFiles.length > 0 || selectedPreset) {
      await generateGraph();
    }
  };

  const handleReset = () => {
    setNodes([]);
    setEdges([]);
    setError('');
    // Clear saved positions when resetting
    // fileStorage.remove('nodePositions');
    // browserStorage.remove('nodePositions');
    // browserStorage.remove('uploadedFiles');
  };

  if (isLoading || areStylesOutdated) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          backgroundColor:
            colorMode === 'dark' ? 'rgb(42, 42, 42)' : 'rgb(255, 255, 255)',
          color: colorMode === 'dark' ? PALETTE.dark.text : PALETTE.light.text,
        }}
      >
        <div style={{ fontSize: '14px' }}>Analyzing selected files...</div>
        {showServerWakeMessage && (
          <div
            style={{
              marginTop: '8px',
              fontSize: '12px',
              maxWidth: '320px',
              textAlign: 'center',
              opacity: 0.8,
            }}
          >
            Server might be asleep. The first load can take up to 50 seconds.
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          backgroundColor:
            colorMode === 'dark' ? 'rgb(42, 42, 42)' : 'rgb(255, 255, 255)',
          color: colorMode === 'dark' ? PALETTE.dark.text : PALETTE.light.text,
        }}
      >
        <div
          style={{
            fontSize: '18px',
            lineHeight: '1.5',
            color: 'red',
            marginBottom: '10px',
            textAlign: 'center',
          }}
        >
          Error: {error}
        </div>
        <button
          onClick={handleReset}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Try Again
        </button>
      </div>
    );
  }



  // Check for demo parameter and show message if no files/nodes are loaded
  const urlParams = new URLSearchParams(window.location.search);
  const demoParam = urlParams.get('demo');
  
  if (!isLoading && !nodes.length) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          backgroundColor:
            colorMode === 'dark' ? 'rgb(42, 42, 42)' : 'rgb(255, 255, 255)',
          color: colorMode === 'dark' ? PALETTE.dark.text : PALETTE.light.text,
        }}
      >
        <div
          style={{
            fontSize: '18px',
            lineHeight: '1.5',
            color: colorMode === 'dark' ? PALETTE.dark.text : PALETTE.light.text,
            marginBottom: '20px',
            textAlign: 'center',
            maxWidth: '500px',
          }}
        >
          Couldn't access the source files for this demo. 
          <br />
          Please try again.
        </div>
        <button
          onClick={() => window.location.href = 'https://codemaps.silverveskilt.com'}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#0056b3';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#007bff';
          }}
        >
          Back to Homepage
        </button>
      </div>
    );
  }


  return (
    <div style={{ height: '100vh' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesState}
        onConnect={onConnect}
        onMove={onMove}
        // className="react-flow-subflows-example"
        fitView
        minZoom={0.1}
        maxZoom={1.5}
        colorMode={colorMode}
        nodeTypes={nodeTypes}
        // snapToGrid={true}
        snapGrid={snapGrid}
      >
        <MiniMap />
        <Controls />
        <Background
          color='#666'
          bgColor={colorMode === 'dark' ? '#2a2a2a' : '#f5f5f5'}
        />

      </ReactFlow>
    </div>
  );
};

export default NestedFlowDemo2;
